import React, { Component, createRef, useState, useEffect } from "react";
import { Row, Col, Card, Button, Space, Modal, Table, Input, Tag, DatePicker } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint, faBinoculars, faDollarSign, faFileDownload, faPlus } from "@fortawesome/free-solid-svg-icons";
import { CSVLink } from "react-csv";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import dayjs from "dayjs";
import _ from "lodash";
import WindowedSelect from "react-windowed-select";

function Invoices(props) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState("");

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [invoicesFilters, setInvoicesFilters] = useState({ status: { label: "All Statuses", value: 0 }, start_date: dayjs().subtract(2, "months"), end_date: dayjs() });

  const invoices_table_columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, row) => (
        <Link to={"/invoicing/" + row.tag}>
          <Tag>#{text}</Tag>
        </Link>
      ),
      sorter: (a, b) => (a.name > b.name ? 1 : -1),
      defaultSortOrder: "descend",
    },
    {
      title: "Client",
      dataIndex: "clients_name",
      key: "clients_name",
      sorter: (a, b) => (a.clients_name > b.clients_name ? 1 : -1),
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiced_at",
      key: "invoiced_at",
      render: (value) => props.MakeDateTimeReadable(value),
      sorter: (a, b) => (dayjs(a.invoiced_at) > dayjs(b.invoiced_at) ? 1 : -1),
    },
    {
      title: "Due Date",
      dataIndex: "due_at",
      key: "due_at",
      render: (value) => props.MakeDateTimeReadable(value),
      sorter: (a, b) => (dayjs(a.due_at) > dayjs(b.due_at) ? 1 : -1),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (value === 1 ? <Tag color="#108ee9">Proposed</Tag> : <Tag color="#87d068">Finalized</Tag>),
    },
    {
      title: "Amount",
      dataIndex: "total",
      key: "total",
      align: "right",
      render: (value) => props.FormatNumberToCurrency(value),
    },
    {
      title: "Exported At",
      dataIndex: "exported_at",
      key: "exported_at",
      render: (value) => props.MakeDateTimeReadable(value),
    },
    {
      title: "Actions",
      align: "right",
      dataIndex: "id",
      key: "id",
      render: (text, row) => (
        <Space>
          <Link to={"/invoicing/" + row.tag}>
            <Button size="small" type="primary">
              <FontAwesomeIcon icon={faBinoculars} className="mr-1" />
              View
            </Button>
          </Link>
          <Link
            style={{ textDecoration: "none" }}
            to={{
              pathname: "/invoice-preview/" + row.tag,
            }}
          >
            <Button size="small">
              <FontAwesomeIcon icon={faPrint} className="mr-1" />
              Print
            </Button>
          </Link>
          <Link to={"/portal/payments-direct/c/" + row.clients_tag + "/i/" + row.tag}>
            <Button size="small">
              <FontAwesomeIcon icon={faDollarSign} className="mr-1" />
              Take Payment
            </Button>
          </Link>
        </Space>
      ),
    },
  ];

  const GetInvoices = async () => {
    let response = await props.AxiosRequest("/api/invoices/get", { ...invoicesFilters, status: invoicesFilters.status.value });
    setInvoices(response.data.invoices);
    FilterInvoices();
  };
  const AddInvoiceAndGoToInvoice = async () => {
    let request = await props.AxiosRequest("/api/invoices/insert", { clients_xref: selectedClient.id });
    navigate("/invoicing/" + request.data.tag);
  };
  const FilterInvoices = async () => {
    if (invoiceSearchTerm?.length === 0) return setFilteredInvoices(invoices);
    let filtered_list = invoices.filter((f) =>
      _.toLower(f.name).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.clients_name).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.invoiced_at).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.due_at).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.exported_at).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(props.MakeDateTimeReadable(f.invoiced_at)).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(props.MakeDateTimeReadable(f.due_at)).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(props.MakeDateTimeReadable(f.exported_at)).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.status_name).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.total).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(props.FormatNumberToCurrency(f.total)).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.job_name).indexOf(_.toLower(invoiceSearchTerm)) > -1
        ? true
        : false,
    );
    setFilteredInvoices(filtered_list);
  };
  const GetClients = async () => {
    let response = await props.AxiosRequest("/api/clients/get/all/simple");
    setClients(
      response.data.clients.map((m) => {
        return { ...m, label: m.name, value: m.id };
      }),
    );
  };

  useEffect(() => {
    GetInvoices();
    GetClients();
  }, []);

  useEffect(() => {
    FilterInvoices();
  }, [invoiceSearchTerm, invoices]);

  useEffect(() => {
    GetInvoices();
  }, [invoicesFilters]);

  return (
    <main className="content">
      <div className="container-fluid">
        <div className="header">
          <h1 className="header-title">Invoices</h1>
        </div>
        <Row>
          <Card
            title={<>Invoices</>}
            style={{
              width: "100%",
            }}
            extra={
              <>
                <Button
                  type="primary"
                  onClick={() => {
                    setModalOpen(true);
                    setSelectedClient(null);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add A New Invoice
                </Button>
              </>
            }
          >
            <Row>
              <Col span={6}>
                <label className="col-form-label-sm">Invoice Date Range</label>
                <DatePicker.RangePicker
                  allowClear={false}
                  value={[invoicesFilters.start_date, invoicesFilters.end_date]}
                  onChange={(e) => {
                    setInvoicesFilters({ ...invoicesFilters, start_date: e[0], end_date: e[1] });
                  }}
                  format="MM/DD/YYYY"
                />
              </Col>
              <Col span={6}>
                <label className="col-form-label-sm">Status</label>
                <Select
                  value={invoicesFilters.status}
                  onChange={(e) => {
                    setInvoicesFilters({ ...invoicesFilters, status: e });
                  }}
                  options={[
                    { label: "All Statuses", value: 0 },
                    { label: "Proposed", value: 1 },
                    { label: "Finalized", value: 2 },
                  ]}
                />
              </Col>
              <Col span={6}></Col>
              <Col span={6}>
                <label className="col-form-label-sm">&nbsp;</label>
                <Input
                  className="mb-2"
                  value={invoiceSearchTerm}
                  onChange={(e) => {
                    setInvoiceSearchTerm(e.target.value);
                  }}
                  placeholder="Search..."
                  allowClear
                />
              </Col>
            </Row>
            <Table className="table table-striped hover" columns={invoices_table_columns} dataSource={filteredInvoices} />
            <Row gutter={[16, 16]}>
              <Col span={12}></Col>
              <Col span={12} className="text-right">
                <Space>
                  <CSVLink filename="Invoices.csv" data={filteredInvoices.map(({ children, key, special, ...keepAttrs }) => keepAttrs)}>
                    <Button type="primary">
                      <FontAwesomeIcon icon={faFileDownload} className="mr-2" /> Download CSV File
                    </Button>
                  </CSVLink>
                  <Link to="/bulk-invoice-preview" state={{ invoices: filteredInvoices.map((m) => m.id) }}>
                    <Button>
                      <FontAwesomeIcon icon={faPrint} className="mr-2" /> Bulk Print Invoices
                    </Button>
                  </Link>
                </Space>
              </Col>
            </Row>
          </Card>
        </Row>
      </div>
      <Modal
        title="Add A New Invoice"
        centered
        destroyOnClose={true}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setSelectedClient(null);
        }}
        footer={
          <>
            <Button
              onClick={() => {
                setModalOpen(false);
              }}
            >
              Cancel
            </Button>
            {selectedClient ? (
              <Button
                type="primary"
                onClick={() => {
                  AddInvoiceAndGoToInvoice();
                }}
              >
                Add Invoice
              </Button>
            ) : (
              <Button type="primary" disabled>
                Add Invoice
              </Button>
            )}
          </>
        }
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <label className="col-form-label-sm">Client *</label>
            <WindowedSelect
              value={selectedClient}
              options={props.MakeClientOptions(clients)}
              onChange={(e) => {
                setSelectedClient(e);
              }}
            />
          </Col>
        </Row>
      </Modal>
    </main>
  );
}

export default Invoices;
